import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router-dom';
import AppRoutes from './routes/AppRoutes';
import { getAppConfig } from './runtimeConfig';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

export default function App() {
  const appConfig = getAppConfig();

  const missingConfig = [
    ['AUTH0_DOMAIN', appConfig.auth0Domain],
    ['AUTH0_CLIENT_ID', appConfig.auth0ClientId],
    ['AUTH0_AUDIENCE', appConfig.auth0Audience],
  ]
    .filter(([, value]) => !value)
    .map(([key]) => key);

  if (missingConfig.length > 0) {
    if (import.meta.env.DEV) {
      // Keep details in dev logs only; avoid exposing config internals in UI.
      console.error('Missing required runtime config keys:', missingConfig);
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-white border border-red-200 rounded-xl p-6">
          <h1 className="text-lg font-semibold text-red-700 mb-2">Application failed to start</h1>
          <p className="text-sm text-gray-700 mb-3">
            A required runtime configuration value is missing. Please check deployment configuration.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Auth0Provider
      domain={appConfig.auth0Domain!}
      clientId={appConfig.auth0ClientId!}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: appConfig.auth0Audience!,
        scope: 'openid profile email',
      }}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Auth0Provider>
  );
}
